/**
 * LifecycleControls — standardizované ovládání kontejnerů / instancí
 *
 * Dva vzory:
 *
 * 1. <LifecycleControls.Row> — ikony v hover skupině (pro řádky v seznamu)
 *    → start/stop/restart/delete jako kompaktní icon tlačítka
 *    → vhodné pro supabase list, apphost list
 *
 * 2. <LifecycleControls.Bar> — text+ikona tlačítka (pro detail panel, expanded karta)
 *    → vhodné pro docker expanded row, apphost detail header
 */

import { useState } from 'react';
import { Loader2, Play, RotateCcw, Square, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { ConfirmDialog } from './ConfirmDialog';

type Action = 'start' | 'stop' | 'restart' | 'delete';

interface RowProps {
  name: string;
  running: boolean;
  pending?: string; // jméno aktuálně probíhající akce
  onAction: (name: string, action: Action) => void;
  canDelete?: boolean;
  className?: string;
}

/** Kompaktní ikony pro řádky v seznamu. Zobrazují se on-hover (group-hover:opacity-100). */

// App-type štítek (volitelný) — barevná mapa; neznámý typ → neutrální šedá.
export type AppType = 'laravel' | 'react' | 'node' | 'static' | 'python' | 'php' | string;
const TYPE_COLORS: Record<string, string> = {
  laravel: 'bg-red-500/10 text-red-400',
  react: 'bg-cyan-500/10 text-cyan-400',
  node: 'bg-emerald-500/10 text-emerald-400',
  python: 'bg-blue-500/10 text-blue-400',
  php: 'bg-purple-500/10 text-purple-400',
  static: 'bg-gray-600/20 text-gray-400',
};
const typeColor = (t: string) => TYPE_COLORS[t] ?? 'bg-gray-600/20 text-gray-400';

export function LifecycleRow(
  { name, pending, onAction, canDelete = true, className }: RowProps,
) {
  const { t } = useTranslation();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const busy = !!pending;

  const iconBtn = (action: Action, icon: React.ReactNode, title: string, hoverCls: string) => (
    <button
      key={action}
      disabled={busy}
      onClick={() => {
        if (action === 'delete') {
          setDeleteOpen(true);
          return;
        }
        onAction(name, action);
      }}
      title={title}
      className={`p-1.5 rounded-lg text-gray-500 transition-colors disabled:opacity-40 ${hoverCls}`}
    >
      {pending === action ? <Loader2 size={13} className='animate-spin' /> : icon}
    </button>
  );

  return (
    <>
      <div className={`flex items-center gap-0.5 ${className ?? ''}`}>
        {iconBtn(
          'start',
          <Play size={13} />,
          t('lifecycle.start'),
          'hover:bg-emerald-900/40 hover:text-emerald-400',
        )}
        {iconBtn(
          'stop',
          <Square size={13} />,
          t('lifecycle.stop'),
          'hover:bg-amber-900/40   hover:text-amber-400',
        )}
        {iconBtn(
          'restart',
          <RotateCcw size={13} />,
          t('lifecycle.restart'),
          'hover:bg-blue-900/40    hover:text-blue-400',
        )}
        {canDelete &&
          iconBtn(
            'delete',
            <Trash2 size={13} />,
            t('lifecycle.delete'),
            'hover:bg-red-900/40 hover:text-red-400',
          )}
      </div>
      {canDelete && (
        <ConfirmDialog
          open={deleteOpen}
          title={t('ui.deleteItemTitle')}
          description={t('ui.deleteNamedConfirm', { name })}
          confirmLabel={t('ui.delete')}
          cancelLabel={t('ui.cancel')}
          danger
          onConfirm={() => {
            setDeleteOpen(false);
            onAction(name, 'delete');
          }}
          onCancel={() => setDeleteOpen(false)}
        />
      )}
    </>
  );
}

interface BarProps {
  name: string;
  running: boolean;
  loading?: boolean;
  onAction: (action: Action) => void;
  appType?: AppType;
  className?: string;
}

/** Text+ikona tlačítka pro detail panel nebo expanded karta. */
export function LifecycleBar({ running, loading, onAction, appType, className }: BarProps) {
  const { t } = useTranslation();
  return (
    <div className={`flex items-center gap-2 ${className ?? ''}`}>
      {!running && (
        <Button
          variant='success'
          size='sm'
          loading={loading}
          icon={<Play size={11} />}
          onClick={() => onAction('start')}
        >
          {t('lifecycle.start')}
        </Button>
      )}
      {running && (
        <>
          <Button
            variant='warning'
            size='sm'
            loading={loading}
            icon={<RotateCcw size={11} />}
            onClick={() => onAction('restart')}
          >
            {t('lifecycle.restart')}
          </Button>
          <Button
            variant='danger'
            size='sm'
            loading={loading}
            icon={<Square size={11} />}
            onClick={() => onAction('stop')}
          >
            {t('lifecycle.stop')}
          </Button>
        </>
      )}
      {appType && (
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded-md font-mono font-medium ${
            typeColor(appType)
          }`}
        >
          {appType}
        </span>
      )}
    </div>
  );
}

export const LifecycleControls = { Row: LifecycleRow, Bar: LifecycleBar };
